import { DMAFileTypes } from 'interfaces/ia.interface.ts';
import { Nullable } from 'interfaces/Nullable.ts';
import apiClient, { AxiosRequestConfig } from 'axios';
import { DateTimeFormat } from '@/utilities/FomattingUtils.ts';
import { redactCredentials, REQUEST_TIMEOUT_MS } from '@/services/apiFetch.ts';

/**
 * Every document request goes to the Files API, carrying the signed-in user's
 * Cognito ID token raw on the `Authorization` header and no credential in any URL.
 *
 *   IADetailsPage / PADetailsPage / QueueContainer
 *      | jwtToken = selectJwtToken (read by the page)
 *      |-- saveFileToPalmettoDB --> POST <files>/api/files/uploadWithoutID   field `file`, 180 s
 *      |-- addOrDeleteFilesFunc --> PUT  <files>/api/files/updateID | delete  30 s
 *      `-- view --> Documents
 *                     |-- list --> GET <files>/api/files  params { filter }   30 s
 *                     |-- tap  --> open blank tab --> resolveFileDownloadURL --> https url ? tab.location : close tab
 *                     `-- FileContainer thumbnail --> resolveFileDownloadURL --> https url ? <img src> : photo icon
 *
 *   resolveFileDownloadURL(uri, jwtToken): GET <files><uri> params { redirect: 'false' } --> { url }   30 s
 *   every request: Authorization: <ID token, raw>; every failure logs redactCredentials(error)
 *
 * `REQUEST_TIMEOUT_MS` and `redactCredentials` are read only inside functions.
 * `apiFetch.ts` reaches this module back through `authToken.session.ts` and the
 * `@/utilities` barrel, so at module scope they can still be uninitialized.
 */
export const filesApiBaseURL: string = import.meta.env.VITE_APP_FILES_API;
if (!filesApiBaseURL) {
  console.error('🚀 FileUploadAPI: VITE_APP_FILES_API is not set -- every file request will fail.');
}

/**
 * An upload is one multipart POST, and a multi-MB photo on a slow field
 * connection outlasts the 30s `REQUEST_TIMEOUT_MS`. Same deadline as
 * `PARCEL_DOWNLOAD_TIMEOUT_MS` in `hooks/useParcelData.ts`.
 */
const FILES_UPLOAD_TIMEOUT_MS = 180000;

/**
 * Fixed string with no interpolated request detail, so a missing credential is
 * diagnosable from the console without the token itself ever being logged.
 */
const missingFilesJwtWarning =
  'FileUploadAPI: authToken.jwtToken is empty, sending no Authorization header. ' +
  'The Files API rejects every document request until the session carries a ' +
  'Cognito ID token again.';

/**
 * Headers for one Files API request: `headers` plus the ID token, raw with no
 * `Bearer` prefix as `apiFetch.ts` sends it. An empty token sends no
 * `Authorization` at all, so the request fails with the host's 401 and takes
 * the caller's normal failure path.
 */
export function filesApiHeaders(
  jwtToken: Nullable<string>,
  headers: Record<string, string> = {}
): Record<string, string> {
  if (!jwtToken) {
    console.warn(missingFilesJwtWarning);
    return headers;
  }
  return { ...headers, Authorization: jwtToken };
}

export function bytesToSize(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  if (Number(bytes) === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return Math.round(value) + ' ' + sizes[i];
}

export const getProperKeyNameDummySearchString = (name: string) => {
  if (name && name.indexOf('_DUMMYSEARCHSTRING_') >= 0) {
    return name.substring(name.indexOf('_DUMMYSEARCHSTRING_') + 19);
  }
  return name;
};

type AddOrDeleteFilesFuncProps = {
  filesToAdd: DMAFileTypes[];
  filesToDelete: DMAFileTypes[];
  uuid: string;
  jwtToken: Nullable<string>;
};
export const addOrDeleteFilesFunc = async ({
  filesToAdd = [],
  filesToDelete = [],
  uuid,
  jwtToken,
}: AddOrDeleteFilesFuncProps) => {
  // Each call logs its own failure: `Promise.all` only surfaces the first one,
  // and the raw axios error it would carry holds the `Authorization` header.
  const putFileChange = (route: string, body: Record<string, unknown>) =>
    apiClient
      .put(`${filesApiBaseURL}${route}`, body, {
        headers: filesApiHeaders(jwtToken, { 'Content-Type': 'application/json' }),
        timeout: REQUEST_TIMEOUT_MS,
      })
      .catch((error: unknown) => {
        console.error(`🚀 addOrDeleteFilesFunc PUT ${route} error: `, redactCredentials(error));
        throw new Error(`Files API PUT ${route} failed`);
      });

  const calls = [];
  for (let i = 0; i < filesToAdd.length; i++) {
    const obj = {
      pvDataID: filesToAdd[i].pvDataID,
      pvGlobalGroupID: uuid,
    };
    calls.push(putFileChange('/api/files/updateID', obj));
  }

  for (let i = 0; i < filesToDelete.length; i++) {
    const obj = {
      globalid: filesToDelete[i].pvGlobalID,
    };
    calls.push(putFileChange('/api/files/delete', obj));
  }

  await Promise.all(calls);

  return filesToAdd.length - filesToDelete.length;
};

/**
 * Warns once per file when an upload-response-derived field the caller relies
 * on is missing, since the fields below are read with a silent `?? ''` / `?? 0`
 * fallback that would otherwise persist blank metadata with no diagnostic trail.
 */
function warnIfUploadFieldsMissing(file: DMAFileTypes, formType: 'ia' | 'pa'): void {
  const missing = (
    ['cbrnDataFileName', 'cbrnDataFileSize', 'cbrnMIMEContentCodeSimpleType', 'pvGlobalID'] as const
  ).filter((key) => file[key] == null);
  if (missing.length > 0) {
    console.warn(`🚀 ${formType} file record missing expected fields: ${missing.join(', ')}`, {
      pvDataID: file.pvDataID,
    });
  }
}

/**
 * Nested `files[]` rows for an IA write.
 *
 * These rows carry no `user_id` by design, not by oversight. The DMA API's
 * identity stamp reaches only the top-level `user_id` on a request body, and its
 * `ia` and `ia_state` handlers persist nested rows without stamping them
 * (`damageassessment-api/MIGRATION-cognito-identity-stamping.md`, "Known caveat
 * -- nested rows still client-asserted"). Adding an attribution here would be a
 * client assertion the server neither verifies nor overwrites, so it waits for
 * the backend follow-up that note tracks.
 */
export const prepareDMAFilesData = (files: DMAFileTypes[], formType: 'ia' | 'pa') => {
  const arr: {
    file_key: string;
    file_size: number;
    file_type: string;
    file_name: string;
    form_type: 'ia' | 'pa';
    pvGlobalID: string;
  }[] = [];

  files.forEach((file) => {
    warnIfUploadFieldsMissing(file, formType);
    arr.push({
      file_key: file.cbrnDataFileName ?? '',
      file_size: file.cbrnDataFileSize ?? 0,
      file_type: file.cbrnMIMEContentCodeSimpleType ?? '',
      file_name: file.cbrnDataFileName ?? '',
      form_type: formType,
      pvGlobalID: file.pvGlobalID ?? '',
    });
  });

  return arr;
};

/**
 * Nested `files[]` rows for a PA write.
 *
 * `user_id` stays client-asserted here by design. `POST`/`PUT /api/pa` do stamp
 * nested rows with the verified account id (`stampFileOwnership` in the API's
 * `api/common/identity.js`), but `/api/pa_state` -- where a state user's PA
 * write lands -- persists them unstamped, so on that route this value is the
 * only attribution the row gets (`damageassessment-api/MIGRATION-cognito-identity-stamping.md`,
 * "Known caveat -- nested rows still client-asserted"). It is left alone,
 * including the `?? 0` fallback below, until the backend follow-up that note
 * tracks lands. The top-level `user_id` is the one the server now owns; see
 * `mergeServerAttribution` in `utilities/pouchDB.ts`.
 */
export const preparePAFilesData = (files: DMAFileTypes[]) => {
  const arr: {
    file_key: string;
    file_size: number;
    file_type: string;
    deleted: number;
    file_name: string;
    user_id: number;
    pvGlobalID: string;
    updated_at: string;
    created_at: string;
    id: string | null;
  }[] = [];

  files.forEach((file) => {
    warnIfUploadFieldsMissing(file, 'pa');
    if (file.pvUserID == null) {
      console.warn('🚀 pa file record missing pvUserID -- attribution defaults to 0', {
        pvDataID: file.pvDataID,
      });
    }
    arr.push({
      id: file.form_id ?? null,
      file_key: file.cbrnDataFileName ?? '',
      file_size: file.cbrnDataFileSize ?? 0,
      file_type: file.cbrnMIMEContentCodeSimpleType ?? '',
      file_name: file.cbrnDataFileName ?? '',
      user_id: file.pvUserID ?? 0,
      deleted: file.deleted ?? 0,
      pvGlobalID: file.pvGlobalID ?? '',
      updated_at: DateTimeFormat({ dte: file.updated_at, format: 'YYYY-MM-DD HH:mm:ss' }),
      created_at: DateTimeFormat({ dte: file.created_at, format: 'YYYY-MM-DD HH:mm:ss' }),
    });
  });

  return arr;
};

/**
 * Uploads one file; the save that follows links it to its record through
 * `addOrDeleteFilesFunc`'s `updateID` call.
 *
 *   happy:          2xx carrying `pvDataID` -> the uploaded row
 *   nil/null:       no ID token -> sent without `Authorization`, 401, upstream error path
 *   empty/zero:     2xx with no `pvDataID` -> logged, `Error('Error uploading files')`
 *   upstream error: error status, 180s timeout, network -> redacted log, same Error
 */
export const saveFileToPalmettoDB = async (
  file: File,
  jwtToken: Nullable<string>,
  uploadForm: <U>(
    file: File,
    url: string,
    config: AxiosRequestConfig<any>
  ) => Promise<{ status: number; data: U }>
) => {
  const url = `${filesApiBaseURL}/api/files/uploadWithoutID`;
  // Nothing beyond these two headers and no query string: the host's CORS allow
  // list has no custom headers, and a raw filter in the URL is rejected with 400.
  const config: AxiosRequestConfig = {
    headers: filesApiHeaders(jwtToken, { 'Content-Type': 'multipart/form-data' }),
    timeout: FILES_UPLOAD_TIMEOUT_MS,
  };

  const uploadedFile = await uploadForm<any>(file, url, config).catch((error: unknown) => {
    console.error('🚀 saveFileToPalmettoDB upload error: ', redactCredentials(error));
    throw new Error('Error uploading files');
  });

  // Only a response carrying the new row's id counts as saved: `updateID` links
  // the file to its record by that id, so without one the file is orphaned.
  if (!uploadedFile?.data?.pvDataID) {
    console.error('🚀 saveFileToPalmettoDB: upload response carried no pvDataID', {
      method: 'post',
      url,
      status: uploadedFile?.status,
    });
    throw new Error('Error uploading files');
  }
  if (uploadedFile.data.cbrnMIMEContentCodeSimpleType?.includes('image')) {
    uploadedFile.data.downloadLink = URL.createObjectURL(file);
  }

  return uploadedFile.data;
};

/** True only for an absolute `https:` URL. */
function isHttpsURL(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    // Unparseable is refused like any other non-https value. Nothing is logged
    // here: the value may be a signed link, and the caller logs the refusal.
    return false;
  }
}

/**
 * A short-lived signed link for one stored file, or null when none is usable.
 * `redirect=false` makes the Files API answer `{ url }` instead of redirecting,
 * because a new tab or an `<img src>` cannot send the `Authorization` header.
 *
 *   happy:          2xx with an https `url` -> that url
 *   nil/null:       no stored path -> null with no request (callers skip these first)
 *   empty/zero:     2xx with no `url`, or a non-https one -> logged, null
 *   upstream error: error status, 30s timeout, network -> redacted log, null
 */
export async function resolveFileDownloadURL(
  uri: Nullable<string> | undefined,
  jwtToken: Nullable<string>
): Promise<Nullable<string>> {
  if (!uri) return null;
  // The stored path is appended to the base URL. A value without a leading `/`
  // joins onto the base itself, sending the request and its token to a URL the
  // Files API never issued.
  if (typeof uri !== 'string' || !uri.startsWith('/')) {
    console.error('🚀 resolveFileDownloadURL: stored file path does not start with "/"');
    return null;
  }

  // The server returns this path already encoded -- e.g. an internal slash in
  // the storage key comes back as a pre-escaped `%2F` within one segment.
  // Re-encoding it here would escape that `%` a second time (`%2F` -> `%252F`),
  // so the Files API can no longer match it to the stored key.
  const url = `${filesApiBaseURL}${uri}`;
  try {
    const response = await apiClient.get<{ url?: unknown } | null>(url, {
      params: { redirect: 'false' },
      headers: filesApiHeaders(jwtToken),
      timeout: REQUEST_TIMEOUT_MS,
    });
    const link = response.data?.url;
    if (isHttpsURL(link)) return link;
    // The body stays out of the log: a link in it opens the file for anyone.
    console.error('🚀 resolveFileDownloadURL: response carried no https url', {
      method: 'get',
      url,
      status: response.status,
    });
    return null;
  } catch (error) {
    console.error('🚀 resolveFileDownloadURL error: ', redactCredentials(error));
    return null;
  }
}
